import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useController, useForm, useWatch } from "react-hook-form";
import { useDebounce } from "use-debounce";

import {
  NudgeFrequency,
  type GetNudgePreferencesQuery,
  type UpdateNudgePreferencesInput,
  useGetNudgePreferencesQuery,
  useUpdateUserNudgePreferencesMutation,
} from "@axio/graphql/generated/graphql";
import { useToast } from "../../../../../contexts/Toast/ToastProvider";

const CUSTOM_SNOOZE_OPTION = "Custom...";
const HOUR_IN_MS = 60 * 60 * 1000;
const WEEK_IN_MS = 7 * 24 * HOUR_IN_MS;
const HOUR_TOLERANCE_IN_MS = 15 * 60 * 1000;
const WEEK_TOLERANCE_IN_MS = 12 * HOUR_IN_MS;
const AUTOSAVE_DEBOUNCE_MS = 600;
const DEFAULT_CANONICAL_TIMEZONE_BUCKET = "America/New_York";
const FACULTY_NUDGE_SETTINGS_ENABLED = false;

const CANONICAL_US_TIMEZONE_BUCKET_MAP: Record<string, string> = {
  Eastern: "America/New_York",
  "New York": "America/New_York",
  "America/New_York": "America/New_York",
  "America/Detroit": "America/New_York",
  "America/Indiana/Indianapolis": "America/New_York",
  "America/Kentucky/Louisville": "America/New_York",
  Central: "America/Chicago",
  "America/Chicago": "America/Chicago",
  "America/Indiana/Knox": "America/Chicago",
  "America/Menominee": "America/Chicago",
  Mountain: "America/Denver",
  "America/Denver": "America/Denver",
  "America/Boise": "America/Denver",
  "America/Phoenix": "America/Denver",
  Pacific: "America/Los_Angeles",
  "America/Los_Angeles": "America/Los_Angeles",
  Alaska: "America/Anchorage",
  "America/Anchorage": "America/Anchorage",
  "America/Juneau": "America/Anchorage",
  "America/Nome": "America/Anchorage",
  Hawaii: "Pacific/Honolulu",
  "Pacific/Honolulu": "Pacific/Honolulu",
  UTC: "UTC",
  "Etc/UTC": "UTC",
};

const ALLOWED_NOTIFICATION_FREQUENCIES = [
  NudgeFrequency.Daily,
  NudgeFrequency.EveryOtherDay,
  NudgeFrequency.Weekly,
] as const;

export type NotificationFrequency = (typeof ALLOWED_NOTIFICATION_FREQUENCIES)[number];
export type ActiveSnoozeOption = "1 hour" | "1 week" | typeof CUSTOM_SNOOZE_OPTION | null;

export type NotificationCategoryToggles = {
  courseUpdates: boolean;
  facultyUpdates: boolean;
};

export type NotificationCategoryKey = keyof NotificationCategoryToggles;

const EMPTY_CATEGORY_TOGGLES: NotificationCategoryToggles = {
  courseUpdates: false,
  facultyUpdates: false,
};

type NotificationPreferencesFormValues = {
  emailEnabled: boolean;
  textMessagesEnabled: boolean;
  smsConsent: boolean;
  smsPhoneNumber: string;
  frequency: NotificationFrequency;
  maxEmailPerDay: number;
  maxSmsPerDay: number;
  preferredSendTime: string;
  timezone: string;
  snoozeAllUntil: string | null;
  categoryToggles: NotificationCategoryToggles;
};

type NotificationPayloadInput = Pick<
  NotificationPreferencesFormValues,
  | "emailEnabled"
  | "textMessagesEnabled"
  | "smsConsent"
  | "categoryToggles"
  | "frequency"
  | "maxEmailPerDay"
  | "maxSmsPerDay"
  | "preferredSendTime"
  | "timezone"
  | "snoozeAllUntil"
>;

const EMPTY_NOTIFICATION_FORM_VALUES: NotificationPreferencesFormValues = {
  emailEnabled: false,
  textMessagesEnabled: false,
  smsConsent: false,
  smsPhoneNumber: "",
  frequency: NudgeFrequency.Daily,
  maxEmailPerDay: 0,
  maxSmsPerDay: 1,
  preferredSendTime: "08:00",
  timezone: DEFAULT_CANONICAL_TIMEZONE_BUCKET,
  snoozeAllUntil: null,
  categoryToggles: EMPTY_CATEGORY_TOGGLES,
};

type HydratedNudgePreferences = NonNullable<NonNullable<GetNudgePreferencesQuery["getNudgePreferences"]["data"]>>;

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  return "Failed to update notification setting";
};

const getDetectedTimezoneBucket = (): string => {
  try {
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return CANONICAL_US_TIMEZONE_BUCKET_MAP[detectedTimezone] ?? DEFAULT_CANONICAL_TIMEZONE_BUCKET;
  } catch {
    return DEFAULT_CANONICAL_TIMEZONE_BUCKET;
  }
};

const toCanonicalTimezoneBucket = (value?: string | null): string => {
  if (!value) {
    return getDetectedTimezoneBucket();
  }

  return CANONICAL_US_TIMEZONE_BUCKET_MAP[value] ?? getDetectedTimezoneBucket();
};

const isNotificationFrequency = (value: string): value is NotificationFrequency => {
  return ALLOWED_NOTIFICATION_FREQUENCIES.includes(value as NotificationFrequency);
};

const normalizeNotificationFrequency = (value?: string | null): NotificationFrequency => {
  if (!value) {
    return NudgeFrequency.Daily;
  }

  if (isNotificationFrequency(value)) {
    return value;
  }

  return NudgeFrequency.Daily;
};

const normalizeSnoozeIso = (value: unknown): string | null => {
  if (!value || typeof value !== "string") {
    return null;
  }

  const parsedValue = new Date(value);
  if (Number.isNaN(parsedValue.getTime())) {
    return null;
  }

  return parsedValue.toISOString();
};

const buildPayloadFromValues = ({
  emailEnabled,
  textMessagesEnabled,
  smsConsent,
  categoryToggles,
  frequency,
  maxEmailPerDay,
  maxSmsPerDay,
  preferredSendTime,
  timezone,
  snoozeAllUntil,
}: NotificationPayloadInput): UpdateNudgePreferencesInput => {
  return {
    emailEnabled,
    smsEnabled: textMessagesEnabled,
    smsConsent,
    courseEnabled: categoryToggles.courseUpdates,
    facultyEnabled: FACULTY_NUDGE_SETTINGS_ENABLED ? categoryToggles.facultyUpdates : false,
    frequency,
    maxEmailPerDay,
    maxSmsPerDay,
    preferredSendTime,
    timezone: toCanonicalTimezoneBucket(timezone),
    snoozeAllUntil: normalizeSnoozeIso(snoozeAllUntil),
  };
};

const getActiveSnoozeOption = (snoozeAllUntil?: string | null): ActiveSnoozeOption => {
  if (!snoozeAllUntil) {
    return null;
  }

  const snoozeAllUntilDate = new Date(snoozeAllUntil);
  if (Number.isNaN(snoozeAllUntilDate.getTime())) {
    return null;
  }

  const remainingMs = snoozeAllUntilDate.getTime() - Date.now();
  if (remainingMs <= 0) {
    return null;
  }

  if (Math.abs(remainingMs - HOUR_IN_MS) <= HOUR_TOLERANCE_IN_MS) {
    return "1 hour";
  }

  if (Math.abs(remainingMs - WEEK_IN_MS) <= WEEK_TOLERANCE_IN_MS) {
    return "1 week";
  }

  return CUSTOM_SNOOZE_OPTION;
};

const createFormValuesFromHydratedPreferences = ({
  preferences,
  phoneNumber,
}: {
  preferences: HydratedNudgePreferences;
  phoneNumber?: string | null;
}): NotificationPreferencesFormValues => {
  const rawPreferences = preferences as unknown as { smsConsent?: boolean | null };

  return {
    emailEnabled: preferences.emailEnabled,
    textMessagesEnabled: preferences.smsEnabled,
    smsConsent: rawPreferences.smsConsent ?? false,
    smsPhoneNumber: phoneNumber ?? "",
    frequency: normalizeNotificationFrequency(preferences.frequency),
    maxEmailPerDay: preferences.maxEmailPerDay,
    maxSmsPerDay: preferences.maxSmsPerDay,
    preferredSendTime: preferences.preferredSendTime,
    timezone: toCanonicalTimezoneBucket(preferences.timezone),
    snoozeAllUntil: normalizeSnoozeIso(preferences.snoozeAllUntil),
    categoryToggles: {
      courseUpdates: preferences.courseEnabled,
      facultyUpdates: FACULTY_NUDGE_SETTINGS_ENABLED ? preferences.facultyEnabled : false,
    },
  };
};

export const useNotificationPreferencesState = () => {
  const { showToast } = useToast();
  const [hasShownPreferencesLoadError, setHasShownPreferencesLoadError] = useState(false);
  const [hasHydratedPreferences, setHasHydratedPreferences] = useState(false);
  const [canPersistChanges, setCanPersistChanges] = useState(false);
  const [animationsEnabled, setAnimationsEnabled] = useState(false);
  const lastSentSerializedPayloadRef = useRef("");
  const inFlightSerializedPayloadRef = useRef<string | null>(null);

  const form = useForm<NotificationPreferencesFormValues>({
    defaultValues: EMPTY_NOTIFICATION_FORM_VALUES,
  });
  const { control, getValues, reset } = form;

  const watchedValues = useWatch({ control });
  const formValues = useMemo<NotificationPreferencesFormValues>(() => {
    return {
      ...EMPTY_NOTIFICATION_FORM_VALUES,
      ...watchedValues,
      categoryToggles: {
        ...EMPTY_CATEGORY_TOGGLES,
        ...(watchedValues?.categoryToggles ?? EMPTY_CATEGORY_TOGGLES),
      },
    };
  }, [watchedValues]);

  const { field: emailEnabledField } = useController({ control, name: "emailEnabled" });
  const { field: textMessagesEnabledField } = useController({ control, name: "textMessagesEnabled" });
  const { field: smsConsentField } = useController({ control, name: "smsConsent" });
  const { field: smsPhoneNumberField } = useController({ control, name: "smsPhoneNumber" });
  const { field: frequencyField } = useController({ control, name: "frequency" });
  const { field: maxEmailPerDayField } = useController({ control, name: "maxEmailPerDay" });
  const { field: maxSmsPerDayField } = useController({ control, name: "maxSmsPerDay" });
  const { field: preferredSendTimeField } = useController({ control, name: "preferredSendTime" });
  const { field: timezoneField } = useController({ control, name: "timezone" });
  const { field: snoozeAllUntilField } = useController({ control, name: "snoozeAllUntil" });
  const { field: courseUpdatesField } = useController({ control, name: "categoryToggles.courseUpdates" });
  const { field: facultyUpdatesField } = useController({ control, name: "categoryToggles.facultyUpdates" });

  const [updateUserNudgePreferencesMutation] = useUpdateUserNudgePreferencesMutation();
  const {
    data: nudgePreferencesData,
    error: nudgePreferencesError,
    loading: nudgePreferencesLoading,
    refetch: refetchNudgePreferences,
  } = useGetNudgePreferencesQuery({ fetchPolicy: "network-only" });
  const activeSmsPhoneNumber = nudgePreferencesData?.me?.phoneNumber ?? "";
  const smsCountryCode = nudgePreferencesData?.me?.countryCode ?? "1";
  const isSmsPhoneVerified = nudgePreferencesData?.me?.isPhoneVerified ?? false;

  useEffect(() => {
    if (!nudgePreferencesError || hasShownPreferencesLoadError) {
      return;
    }

    setHasShownPreferencesLoadError(true);
    setHasHydratedPreferences(true);
    setCanPersistChanges(false);
    showToast({
      message: getErrorMessage(nudgePreferencesError),
      type: "error",
    });
  }, [hasShownPreferencesLoadError, nudgePreferencesError, showToast]);

  useEffect(() => {
    if (hasHydratedPreferences) {
      return;
    }

    const nudgePreferencesResult = nudgePreferencesData?.getNudgePreferences;
    if (!nudgePreferencesResult) {
      return;
    }

    if (!nudgePreferencesResult.success || !nudgePreferencesResult.data) {
      if (nudgePreferencesResult.message && !hasShownPreferencesLoadError) {
        setHasShownPreferencesLoadError(true);
        showToast({
          message: nudgePreferencesResult.message,
          type: "error",
        });
      }

      setHasHydratedPreferences(true);
      setCanPersistChanges(false);
      return;
    }

    const hydratedValues = createFormValuesFromHydratedPreferences({
      preferences: nudgePreferencesResult.data,
      phoneNumber: nudgePreferencesData?.me?.phoneNumber,
    });
    const serializedHydratedPayload = JSON.stringify(buildPayloadFromValues(hydratedValues));

    reset(hydratedValues);
    lastSentSerializedPayloadRef.current = serializedHydratedPayload;
    setHasHydratedPreferences(true);
    setCanPersistChanges(true);
  }, [hasHydratedPreferences, hasShownPreferencesLoadError, nudgePreferencesData, reset, showToast]);

  const payloadFromForm = useMemo(() => {
    if (!hasHydratedPreferences || !canPersistChanges) {
      return null;
    }

    return buildPayloadFromValues(formValues);
  }, [canPersistChanges, formValues, hasHydratedPreferences]);

  const serializedPayloadFromForm = useMemo(() => {
    if (!payloadFromForm) {
      return "";
    }

    return JSON.stringify(payloadFromForm);
  }, [payloadFromForm]);

  const [debouncedSerializedPayload] = useDebounce(serializedPayloadFromForm, AUTOSAVE_DEBOUNCE_MS);

  const persistNotificationPreferences = useCallback(
    async (input: UpdateNudgePreferencesInput, serializedPayload: string) => {
      if (
        !serializedPayload ||
        serializedPayload === lastSentSerializedPayloadRef.current ||
        serializedPayload === inFlightSerializedPayloadRef.current
      ) {
        return;
      }

      inFlightSerializedPayloadRef.current = serializedPayload;

      try {
        const response = await updateUserNudgePreferencesMutation({
          variables: { input },
        });
        const result = response.data?.updateUserNudgePreferences;

        if (!result?.success) {
          showToast({
            message: result?.message ?? "Failed to update notification setting",
            type: "error",
          });
          return;
        }

        lastSentSerializedPayloadRef.current = serializedPayload;
      } catch (error) {
        showToast({
          message: getErrorMessage(error),
          type: "error",
        });
      } finally {
        if (inFlightSerializedPayloadRef.current === serializedPayload) {
          inFlightSerializedPayloadRef.current = null;
        }
      }
    },
    [showToast, updateUserNudgePreferencesMutation]
  );

  useEffect(() => {
    if (!hasHydratedPreferences || !canPersistChanges || !payloadFromForm || !debouncedSerializedPayload) {
      return;
    }

    if (debouncedSerializedPayload !== serializedPayloadFromForm) {
      return;
    }

    void persistNotificationPreferences(payloadFromForm, debouncedSerializedPayload);
  }, [
    canPersistChanges,
    debouncedSerializedPayload,
    hasHydratedPreferences,
    payloadFromForm,
    persistNotificationPreferences,
    serializedPayloadFromForm,
  ]);

  const enableAnimations = useCallback(() => {
    setAnimationsEnabled(true);
  }, []);

  const setSmsPhoneNumber = useCallback(
    (nextValue: string) => {
      smsPhoneNumberField.onChange(nextValue);
    },
    [smsPhoneNumberField]
  );

  const handleEmailNotificationsToggle = useCallback(
    (nextValue: boolean) => {
      enableAnimations();
      emailEnabledField.onChange(nextValue);
    },
    [emailEnabledField, enableAnimations]
  );

  const handleTextMessagesToggle = useCallback(
    (nextValue: boolean) => {
      enableAnimations();
      textMessagesEnabledField.onChange(nextValue);
    },
    [enableAnimations, textMessagesEnabledField]
  );

  const handleSmsConsentChange = useCallback(
    (nextValue: boolean) => {
      enableAnimations();
      smsConsentField.onChange(nextValue);
    },
    [enableAnimations, smsConsentField]
  );

  const handleFrequencyChange = useCallback(
    (nextValue: string) => {
      if (!isNotificationFrequency(nextValue)) {
        return;
      }

      frequencyField.onChange(normalizeNotificationFrequency(nextValue));
    },
    [frequencyField]
  );

  const handleEmailMaxPerDayChange = useCallback(
    (nextValue: string) => {
      const parsedValue = Number.parseInt(nextValue, 10);
      if (Number.isNaN(parsedValue)) {
        return;
      }

      maxEmailPerDayField.onChange(parsedValue);
    },
    [maxEmailPerDayField]
  );

  const handleSmsMaxPerDayChange = useCallback(
    (nextValue: string) => {
      const parsedValue = Number.parseInt(nextValue, 10);
      if (Number.isNaN(parsedValue)) {
        return;
      }

      maxSmsPerDayField.onChange(parsedValue);
    },
    [maxSmsPerDayField]
  );

  const handlePreferredSendTimeChange = useCallback(
    (nextValue: string) => {
      preferredSendTimeField.onChange(nextValue);
    },
    [preferredSendTimeField]
  );

  const handleTimezoneChange = useCallback(
    (nextValue: string) => {
      timezoneField.onChange(toCanonicalTimezoneBucket(nextValue));
    },
    [timezoneField]
  );

  const handlePresetSnoozeOptionPress = useCallback(
    async (option: "1 hour" | "1 week") => {
      const nextDate = new Date();
      if (option === "1 hour") {
        nextDate.setHours(nextDate.getHours() + 1);
      } else {
        nextDate.setDate(nextDate.getDate() + 7);
      }

      snoozeAllUntilField.onChange(nextDate.toISOString());
    },
    [snoozeAllUntilField]
  );

  const handleCustomSnoozeSelect = useCallback(
    async (snoozeUntilIso: string) => {
      const customDate = new Date(snoozeUntilIso);

      if (Number.isNaN(customDate.getTime())) {
        showToast({
          message: "Invalid snooze date",
          type: "error",
        });
        return false;
      }

      snoozeAllUntilField.onChange(customDate.toISOString());
      return true;
    },
    [showToast, snoozeAllUntilField]
  );

  const handleResumeSnooze = useCallback(async () => {
    snoozeAllUntilField.onChange(null);

    if (!canPersistChanges) {
      return;
    }

    const currentValues = getValues();

    const nextValues: NotificationPreferencesFormValues = {
      ...EMPTY_NOTIFICATION_FORM_VALUES,
      ...currentValues,
      snoozeAllUntil: null,
      categoryToggles: {
        ...EMPTY_CATEGORY_TOGGLES,
        ...(currentValues.categoryToggles ?? EMPTY_CATEGORY_TOGGLES),
      },
    };

    const resumePayload = buildPayloadFromValues(nextValues);
    const serializedResumePayload = JSON.stringify(resumePayload);

    await persistNotificationPreferences(resumePayload, serializedResumePayload);
  }, [canPersistChanges, getValues, persistNotificationPreferences, snoozeAllUntilField]);

  const handleCategoryToggle = useCallback(
    (key: NotificationCategoryKey, nextValue: boolean) => {
      enableAnimations();
      const categoryFieldByKey = {
        courseUpdates: courseUpdatesField,
        facultyUpdates: facultyUpdatesField,
      } as const;
      categoryFieldByKey[key].onChange(nextValue);
    },
    [courseUpdatesField, enableAnimations, facultyUpdatesField]
  );

  const handlePhoneVerificationSuccess = useCallback(async () => {
    try {
      await refetchNudgePreferences();
    } catch {
      // Keep the local verified-phone override in place if the refetch fails.
    }
  }, [refetchNudgePreferences]);

  const areAllNudgeCategoriesDisabled = useMemo(() => {
    return (
      !formValues.categoryToggles.courseUpdates &&
      (!FACULTY_NUDGE_SETTINGS_ENABLED || !formValues.categoryToggles.facultyUpdates)
    );
  }, [formValues.categoryToggles]);

  const activeSnooze = getActiveSnoozeOption(formValues.snoozeAllUntil);
  const shouldHoldInitialRender =
    !hasHydratedPreferences && (nudgePreferencesLoading || nudgePreferencesData?.getNudgePreferences != null);

  return {
    emailEnabled: formValues.emailEnabled,
    textMessagesEnabled: formValues.textMessagesEnabled,
    activeSmsPhoneNumber,
    smsConsent: formValues.smsConsent,
    smsPhoneNumber: formValues.smsPhoneNumber,
    smsCountryCode,
    isSmsPhoneVerified,
    frequency: formValues.frequency,
    maxEmailPerDay: formValues.maxEmailPerDay,
    maxSmsPerDay: formValues.maxSmsPerDay,
    preferredSendTime: formValues.preferredSendTime,
    timezone: formValues.timezone,
    snoozeAllUntil: formValues.snoozeAllUntil,
    categoryToggles: formValues.categoryToggles,
    animationsEnabled,
    activeSnooze,
    areAllNudgeCategoriesDisabled,
    shouldHoldInitialRender,
    enableAnimations,
    handlePhoneVerificationSuccess,
    setSmsPhoneNumber,
    handleEmailNotificationsToggle,
    handleTextMessagesToggle,
    handleSmsConsentChange,
    handleFrequencyChange,
    handleEmailMaxPerDayChange,
    handleSmsMaxPerDayChange,
    handlePreferredSendTimeChange,
    handleTimezoneChange,
    handlePresetSnoozeOptionPress,
    handleCustomSnoozeSelect,
    handleResumeSnooze,
    handleCategoryToggle,
  };
};
